import React, { useState, useMemo } from 'react';
import { 
  Box, 
  Autocomplete, 
  TextField, 
  ClickAwayListener,
  createFilterOptions,
} from '@mui/material';
import { Add, Close } from '@mui/icons-material';
import { Tag, TagSchema } from '../types';
import { useCRMTagsContext } from '../CRMTagsContext';
import { FormGrid, SimpleDialog } from '../../primitives';
import { TagChip, TagChipBare } from './CRMTags';
import { FormControlsForFields } from '../../schemed';
import { TagColorPicker } from './TagColorPicker';
import { useEditItem2 } from '../../../api/useNewItem';
import { SmallAddButton } from './Primitives';

export interface ContactTagsProps {
  tagIds?: (string | number)[];
  onUpdateTags: (newTagIds: (string | number)[]) => void;
}

const filter = createFilterOptions<Tag>();

export const ContactTags = (props: ContactTagsProps) => {
  const { tagIds = [], onUpdateTags } = props;
  const { tags, createTag } = useCRMTagsContext();
  
  const [isAdding, setIsAdding] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  
  const newTag = useEditItem2<Partial<Tag>>({
    save: async (item) => {
      const createdTag = await createTag(item);
      setSearchValue("");
      onUpdateTags([...tagIds, createdTag._id]);
      return createdTag;
    }
  });

  const handleRemoveTag = (e: React.MouseEvent, tagToRemove: Tag) => {
    e.stopPropagation();
    onUpdateTags(tagIds.filter(id => id.toString() !== tagToRemove._id.toString()));
  };

  const handleSelectTag = (newValue: string | Tag | null) => {
    if (!newValue) return;

    if (typeof newValue === 'string') {
      return; // Ignore raw string submissions
    }

    if ((newValue as any).inputValue) {
      newTag.startEditing({ name: (newValue as any).inputValue })
      return;
    }

    const selectedId = newValue._id;
    if (!tagIds.some(id => id.toString() === selectedId.toString())) {
      onUpdateTags([...tagIds, selectedId]);
    }
    
    setSearchValue("");
  };

  const availableOptions = tags.filter(
    t => !tagIds.some(id => id.toString() === t._id.toString())
  );

  // We manually compute the filtered options here so we can check their length in onKeyDown
  const filteredOptions = useMemo(() => {
    const options = filter(availableOptions, { inputValue: searchValue, getOptionLabel: (o) => o.name });
    const isExisting = availableOptions.some((option) => searchValue.toLowerCase() === option.name.toLowerCase());
    
    if (searchValue !== '' && !isExisting) {
      options.push({
        inputValue: searchValue,
        name: `Создать "${searchValue}"`,
      } as any);
    }
    return options;
  }, [searchValue, availableOptions]);

  return (
    <Box>
      <Box display="flex" flexWrap="wrap" alignItems={"center"} gap={0.5}>
        {tagIds.map((id) => (
          <TagChip 
            key={id} 
            tagId={id} 
            action={handleRemoveTag} 
            actionIcon={<Close sx={{ fontSize: 12 }} />} 
          />
        ))}
        {!isAdding && (
          <SmallAddButton iconOnly={!!tagIds?.length} icon={<Add />} onClick={() => setIsAdding(true)}>
            добавить тег
          </SmallAddButton>
        )}
      </Box>

      {/* ADD TAG CONTROL */}
      {!!isAdding && (
        <ClickAwayListener onClickAway={() => setIsAdding(false)}>
          <Box sx={{ width: 250, mt: 0.5 }}>
            <Autocomplete
              freeSolo
              openOnFocus
              clearOnBlur
              options={filteredOptions} // Use the manually computed options
              inputValue={searchValue}
              value={null}
              onInputChange={(e, newInputValue) => setSearchValue(newInputValue)}
              onChange={(e, newValue) => handleSelectTag(newValue)}
              getOptionLabel={(option) => {
                if (typeof option === 'string') return option;
                if ((option as any).inputValue) return (option as any).inputValue;
                return option.name;
              }}
              // filterOptions is now just a pass-through since we pre-filter
              filterOptions={(x) => x} 
              renderOption={(props, option) => (
                <li {...props}>
                  {(option as any).inputValue ? (
                    option.name 
                  ) : (
                    <TagChipBare tag={option} />
                  )}
                </li>
              )}
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  autoFocus 
                  size="small" 
                  placeholder="Поиск тегов..."
                  onKeyDown={(e) => {
                    // Pass the event to Autocomplete's internal handler first
                    if (params.inputProps.onKeyDown) {
                      params.inputProps.onKeyDown(e as any);
                    }
                    
                    // If user pressed Enter and there's exactly 1 valid option, select it
                    if (e.key === 'Enter' && searchValue.trim() !== '') {
                      if (filteredOptions.length === 1) {
                        e.preventDefault(); // Prevent standard freeSolo submission
                        handleSelectTag(filteredOptions[0]);
                      }
                    }
                  }}
                />
              )}
            />
          </Box>
        </ClickAwayListener>
      )}

      {/* CREATE NEW TAG DIALOG */}
      {!!newTag.isEditing && (
        <SimpleDialog
          {...newTag.dialog}
          dialogTitle="Новый тег"
          save={() => newTag.save()}
          maxWidth="xs"
          noFullscreen
          submitOnModEnter
        >
          {!!newTag.item &&
            <FormGrid columns="1fr" noMargin>
              <FormControlsForFields
                fields={[
                  ["name", { controlProps: { autoFocus: true }}],
                ]}
                schema={TagSchema}
                data={newTag.item}
                onChange={(o, c) => newTag.update(c)}
              />
              <TagColorPicker
                value={newTag.item.color}
                update={v => newTag.update({ color: v as any })}
                />
              <FormControlsForFields
                fields={[
                  ["description", { controlProps: { multiline: true, rows: 2 } }],
                ]}
                schema={TagSchema}
                data={newTag.item}
                onChange={(o, c) => newTag.update(c)}
              />
            </FormGrid>}
        </SimpleDialog>
      )}
    </Box>
  );
};